import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Settings, 
  RefreshCw, 
  Check, 
  X, 
  ExternalLink,
  Shield,
  Search,
  Clock,
  AlertCircle,
  ChevronRight,
  Mail,
  HardDrive,
  Calendar,
  Video,
  MessageSquare,
  Send,
  Hash,
  FileSpreadsheet,
  Github,
  Cloud,
  FileSignature,
  Zap,
  Link2,
  Unlink
} from 'lucide-react';

// Integration Components
import GmailIntegration from '../components/integrations/GmailIntegration';
import GoogleDriveIntegration from '../components/integrations/GoogleDriveIntegration';
import SlackIntegration from '../components/integrations/SlackIntegration';
import TelegramIntegration from '../components/integrations/TelegramIntegration';
import DiscordIntegration from '../components/integrations/DiscordIntegration';
import RabbitSignIntegration from '../components/integrations/RabbitSignIntegration';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';

const API = process.env.REACT_APP_BACKEND_URL;

// Application configurations
const APPLICATIONS = [
  {
    id: 'gmail',
    name: 'Gmail',
    description: 'Send and receive emails, manage candidate communications',
    icon: Mail,
    color: 'from-red-500 to-red-600',
    bgColor: 'bg-red-50',
    features: ['Send Email', 'Receive Email', 'Email Templates', 'Thread View', 'Auto-match Candidates'],
    category: 'communication',
    freeplan: true,
    oauthRequired: true
  },
  {
    id: 'google_drive',
    name: 'Google Drive',
    description: 'Store resumes, documents, and files in organized folders',
    icon: HardDrive,
    color: 'from-yellow-500 to-orange-500',
    bgColor: 'bg-yellow-50',
    features: ['Auto Folder Creation', 'CV Storage', 'Document Preview', 'File Organization'],
    category: 'storage',
    freeplan: true,
    oauthRequired: true
  },
  {
    id: 'google_calendar',
    name: 'Google Calendar',
    description: 'Schedule interviews and manage recruitment events',
    icon: Calendar,
    color: 'from-blue-500 to-blue-600',
    bgColor: 'bg-blue-50',
    features: ['Interview Scheduling', 'Auto Reminders', 'Team Calendar', 'Event Sync'],
    category: 'scheduling',
    freeplan: true,
    oauthRequired: true
  },
  {
    id: 'google_meet',
    name: 'Google Meet',
    description: 'Create video meeting links for interviews',
    icon: Video,
    color: 'from-green-500 to-teal-500',
    bgColor: 'bg-green-50',
    features: ['Auto Meeting Links', 'Recording Links', 'Participant Tracking'],
    category: 'scheduling',
    freeplan: true,
    oauthRequired: true
  },
  {
    id: 'slack',
    name: 'Slack',
    description: 'Team notifications and recruitment alerts',
    icon: MessageSquare,
    color: 'from-purple-500 to-purple-600',
    bgColor: 'bg-purple-50',
    features: ['Notifications', 'Channel Alerts', 'Mentions', 'HR Updates'],
    category: 'communication',
    freeplan: true,
    oauthRequired: true
  },
  {
    id: 'telegram',
    name: 'Telegram',
    description: 'Bot notifications and candidate announcements',
    icon: Send,
    color: 'from-sky-500 to-blue-500',
    bgColor: 'bg-sky-50',
    features: ['Bot Notifications', 'Group Announcements', 'Candidate Alerts'],
    category: 'communication',
    freeplan: true,
    oauthRequired: false
  },
  {
    id: 'discord',
    name: 'Discord',
    description: 'Recruitment channels and team communication',
    icon: Hash,
    color: 'from-indigo-500 to-indigo-600',
    bgColor: 'bg-indigo-50',
    features: ['Channel Integration', 'Announcements', 'Support Channels'],
    category: 'communication',
    freeplan: true,
    oauthRequired: true
  },
  {
    id: 'google_sheets',
    name: 'Google Sheets',
    description: 'Export reports and candidate lists',
    icon: FileSpreadsheet,
    color: 'from-emerald-500 to-green-500',
    bgColor: 'bg-emerald-50',
    features: ['Report Export', 'Candidate Lists', 'Dashboard Sync'],
    category: 'productivity',
    freeplan: true,
    oauthRequired: true
  },
  {
    id: 'github',
    name: 'GitHub',
    description: 'Track deployments and version history',
    icon: Github,
    color: 'from-gray-700 to-gray-900',
    bgColor: 'bg-gray-100',
    features: ['Repository View', 'Deployment Status', 'Commit History'],
    category: 'development',
    freeplan: true,
    oauthRequired: true
  },
  {
    id: 'dropbox',
    name: 'Dropbox',
    description: 'Alternative cloud storage for documents',
    icon: Cloud,
    color: 'from-blue-600 to-blue-700',
    bgColor: 'bg-blue-50',
    features: ['File Storage', 'Document Preview', 'Folder Sync'],
    category: 'storage',
    freeplan: true,
    oauthRequired: true
  },
  {
    id: 'onedrive',
    name: 'OneDrive',
    description: 'Microsoft cloud storage integration',
    icon: Cloud,
    color: 'from-blue-500 to-cyan-500',
    bgColor: 'bg-cyan-50',
    features: ['File Storage', 'Office Integration', 'Folder Sync'],
    category: 'storage',
    freeplan: true,
    oauthRequired: true
  },
  {
    id: 'rabbitsign',
    name: 'RabbitSign',
    description: 'Electronic signatures for offer letters and agreements',
    icon: FileSignature,
    color: 'from-orange-500 to-red-500',
    bgColor: 'bg-orange-50',
    features: ['E-Signatures', 'Document Tracking', 'Audit Trail', 'Multiple Signers'],
    category: 'documents',
    freeplan: true,
    oauthRequired: true
  }
];

const CATEGORIES = [
  { id: 'all', name: 'All Applications', icon: Zap },
  { id: 'communication', name: 'Communication', icon: MessageSquare },
  { id: 'storage', name: 'Storage', icon: HardDrive },
  { id: 'scheduling', name: 'Scheduling', icon: Calendar },
  { id: 'documents', name: 'Documents', icon: FileSignature },
  { id: 'productivity', name: 'Productivity', icon: FileSpreadsheet },
  { id: 'development', name: 'Development', icon: Github }
];

const ApplicationHub = () => {
  const [integrations, setIntegrations] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedApp, setSelectedApp] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [syncing, setSyncing] = useState({});
  const [activeIntegration, setActiveIntegration] = useState(null); // Track which integration is open

  useEffect(() => {
    fetchIntegrations();
  }, []);

  const fetchIntegrations = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/integrations/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIntegrations(response.data || {});
    } catch (error) {
      console.error('Error fetching integrations:', error);
      // Initialize with empty state if endpoint doesn't exist yet
      setIntegrations({});
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = async (appId) => {
    // For apps with custom views, open them directly
    const appsWithViews = ['gmail', 'google_drive', 'slack', 'telegram', 'discord', 'rabbitsign'];
    if (appsWithViews.includes(appId)) {
      setActiveIntegration(appId);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/api/integrations/${appId}/connect`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.auth_url) {
        // OAuth flow - redirect to auth URL
        window.location.href = response.data.auth_url;
      } else {
        toast.success(`${appId} connected successfully`);
        fetchIntegrations();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to connect');
    }
  };

  const handleDisconnect = async (appId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/integrations/${appId}/disconnect`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`${appId} disconnected`);
      fetchIntegrations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to disconnect');
    }
  };

  const handleSync = async (appId) => {
    setSyncing(prev => ({ ...prev, [appId]: true }));
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/integrations/${appId}/sync`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`${appId} synced successfully`);
      fetchIntegrations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Sync failed');
    } finally {
      setSyncing(prev => ({ ...prev, [appId]: false }));
    }
  };

  const filteredApps = APPLICATIONS.filter(app => {
    const matchesSearch = app.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         app.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || app.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const getAppStatus = (appId) => {
    return integrations[appId] || { connected: false };
  };

  const connectedCount = Object.values(integrations).filter(i => i.connected).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <RefreshCw className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  // Render specific integration views
  if (activeIntegration === 'gmail') {
    return <GmailIntegration onBack={() => setActiveIntegration(null)} />;
  }
  if (activeIntegration === 'google_drive') {
    return <GoogleDriveIntegration onBack={() => setActiveIntegration(null)} />;
  }
  if (activeIntegration === 'slack') {
    return <SlackIntegration onBack={() => setActiveIntegration(null)} />;
  }
  if (activeIntegration === 'telegram') {
    return <TelegramIntegration onBack={() => setActiveIntegration(null)} />;
  }
  if (activeIntegration === 'discord') {
    return <DiscordIntegration onBack={() => setActiveIntegration(null)} />;
  }
  if (activeIntegration === 'rabbitsign') {
    return <RabbitSignIntegration onBack={() => setActiveIntegration(null)} />;
  }

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="application-hub">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-2">
          <div>
            <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
                <Zap className="h-5 w-5 text-white" />
              </div>
              Application Hub
            </h1>
            <p className="text-slate-500 mt-1">Connect and manage your recruitment tools</p>
          </div>
          <div className="flex items-center gap-3">
            <Badge variant="outline" className="px-3 py-1.5 text-sm">
              <Check className="h-3.5 w-3.5 mr-1.5 text-green-500" />
              {connectedCount} Connected
            </Badge>
            <Badge variant="outline" className="px-3 py-1.5 text-sm bg-green-50 text-green-700 border-green-200">
              <Shield className="h-3.5 w-3.5 mr-1.5" />
              Free Plan Only
            </Badge>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="flex flex-col lg:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Search applications..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="app-search"
          />
        </div>
        <div className="flex gap-2 overflow-x-auto pb-2">
          {CATEGORIES.map(cat => (
            <Button
              key={cat.id}
              variant={selectedCategory === cat.id ? 'default' : 'outline'}
              size="sm"
              onClick={() => setSelectedCategory(cat.id)}
              className={`whitespace-nowrap ${selectedCategory === cat.id ? 'bg-blue-600' : ''}`}
            >
              <cat.icon className="h-4 w-4 mr-1.5" />
              {cat.name}
            </Button>
          ))}
        </div>
      </div>

      {/* Zero Storage Notice */}
      <Card className="mb-6 bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
              <Shield className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h3 className="font-semibold text-blue-900">Zero Storage Architecture</h3>
              <p className="text-sm text-blue-700 mt-1">
                All files remain in their original applications (Google Drive, Dropbox, etc.). 
                Officium Connect only stores metadata like file IDs and URLs - never the actual files. 
                This keeps the CRM lightweight, secure, and cost-effective.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Applications Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredApps.map(app => {
          const status = getAppStatus(app.id);
          const AppIcon = app.icon;
          const isSyncing = syncing[app.id];

          return (
            <Card 
              key={app.id} 
              className={`relative overflow-hidden transition-all hover:shadow-lg ${
                status.connected ? 'ring-2 ring-green-500 ring-offset-2' : ''
              }`}
              data-testid={`app-card-${app.id}`}
            >
              {/* Status indicator */}
              {status.connected && (
                <div className="absolute top-3 right-3">
                  <div className="w-3 h-3 rounded-full bg-green-500 animate-pulse" />
                </div>
              )}

              <CardContent className="p-5">
                {/* App Header */}
                <div className="flex items-start gap-4 mb-4">
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${app.color} flex items-center justify-center flex-shrink-0 shadow-lg`}>
                    <AppIcon className="h-6 w-6 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-slate-800 truncate">{app.name}</h3>
                    <p className="text-sm text-slate-500 line-clamp-2">{app.description}</p>
                  </div>
                </div>

                {/* Features */}
                <div className="flex flex-wrap gap-1.5 mb-4">
                  {app.features.slice(0, 3).map((feature, idx) => (
                    <Badge key={idx} variant="secondary" className="text-xs bg-slate-100">
                      {feature}
                    </Badge>
                  ))}
                  {app.features.length > 3 && (
                    <Badge variant="secondary" className="text-xs bg-slate-100">
                      +{app.features.length - 3}
                    </Badge>
                  )}
                </div>

                {/* Status Info */}
                {status.connected && (
                  <div className="flex items-center gap-4 mb-4 text-xs text-slate-500">
                    <span className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      Last sync: {status.last_sync ? new Date(status.last_sync).toLocaleString() : 'Never'}
                    </span>
                  </div>
                )}

                {/* Actions */}
                <div className="flex items-center gap-2">
                  {status.connected ? (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1 text-green-600 border-green-200 hover:bg-green-50"
                        disabled
                      >
                        <Check className="h-4 w-4 mr-1.5" />
                        Connected
                      </Button>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleSync(app.id)}
                              disabled={isSyncing}
                              className="h-9 w-9"
                            >
                              <RefreshCw className={`h-4 w-4 ${isSyncing ? 'animate-spin' : ''}`} />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Sync Now</TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => { setSelectedApp(app); setShowSettings(true); }}
                              className="h-9 w-9"
                            >
                              <Settings className="h-4 w-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Settings</TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDisconnect(app.id)}
                              className="h-9 w-9 text-red-500 hover:text-red-600 hover:bg-red-50"
                            >
                              <Unlink className="h-4 w-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Disconnect</TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </>
                  ) : (
                    <Button
                      className={`flex-1 bg-gradient-to-r ${app.color} hover:opacity-90`}
                      onClick={() => handleConnect(app.id)}
                    >
                      <Link2 className="h-4 w-4 mr-1.5" />
                      Connect
                    </Button>
                  )}
                </div>

                {/* Free Plan Badge */}
                {app.freeplan && (
                  <div className="mt-3 pt-3 border-t border-slate-100">
                    <span className="text-xs text-green-600 flex items-center gap-1">
                      <Check className="h-3 w-3" />
                      Free Plan Supported
                    </span>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Empty State */}
      {filteredApps.length === 0 && (
        <Card className="p-12 text-center">
          <AlertCircle className="h-12 w-12 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-slate-700">No applications found</h3>
          <p className="text-slate-500 mt-1">Try adjusting your search or filter</p>
        </Card>
      )}

      {/* Settings Dialog */}
      <Dialog open={showSettings} onOpenChange={setShowSettings}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              {selectedApp && (
                <>
                  <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${selectedApp.color} flex items-center justify-center`}>
                    <selectedApp.icon className="h-5 w-5 text-white" />
                  </div>
                  {selectedApp.name} Settings
                </>
              )}
            </DialogTitle>
            <DialogDescription>
              Configure your {selectedApp?.name} integration settings
            </DialogDescription>
          </DialogHeader>
          
          {selectedApp && (
            <div className="space-y-4 py-4">
              {/* Connection Status */}
              <div className="p-4 bg-slate-50 rounded-lg">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Connection Status</span>
                  <Badge className={getAppStatus(selectedApp.id).connected ? 'bg-green-500' : 'bg-slate-400'}>
                    {getAppStatus(selectedApp.id).connected ? 'Connected' : 'Disconnected'}
                  </Badge>
                </div>
                {getAppStatus(selectedApp.id).connected && (
                  <div className="mt-3 text-xs text-slate-500 space-y-1">
                    <p>Last Sync: {getAppStatus(selectedApp.id).last_sync || 'Never'}</p>
                    <p>Permissions: {getAppStatus(selectedApp.id).scopes?.join(', ') || 'Standard'}</p>
                  </div>
                )}
              </div>

              {/* Features */}
              <div>
                <h4 className="text-sm font-medium mb-2">Available Features</h4>
                <div className="space-y-2">
                  {selectedApp.features.map((feature, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-sm">
                      <Check className="h-4 w-4 text-green-500" />
                      {feature}
                    </div>
                  ))}
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-2 pt-4 border-t">
                <Button variant="outline" className="flex-1" onClick={() => handleSync(selectedApp.id)}>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Sync Now
                </Button>
                <Button variant="outline" className="flex-1">
                  <Clock className="h-4 w-4 mr-2" />
                  View Activity
                </Button>
              </div>

              {getAppStatus(selectedApp.id).connected && (
                <Button 
                  variant="destructive" 
                  className="w-full"
                  onClick={() => { handleDisconnect(selectedApp.id); setShowSettings(false); }}
                >
                  <Unlink className="h-4 w-4 mr-2" />
                  Disconnect {selectedApp.name}
                </Button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Google Setup Guide */}
      <Card className="mt-8">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-amber-500" />
            Setup Guide: Google Integration
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4 text-sm">
            <p className="text-slate-600">
              To connect Google services (Gmail, Drive, Calendar, Meet), you need to create credentials in Google Cloud Console:
            </p>
            <ol className="list-decimal list-inside space-y-2 text-slate-600">
              <li>Go to <a href="https://console.cloud.google.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Google Cloud Console</a></li>
              <li>Create a new project or select existing one</li>
              <li>Enable these APIs: Gmail API, Google Drive API, Google Calendar API</li>
              <li>Go to Credentials and create OAuth 2.0 Client ID</li>
              <li>Add authorized redirect URI: <code className="bg-slate-100 px-2 py-0.5 rounded">{window.location.origin}/auth/google/callback</code></li>
              <li>Copy the Client ID and Client Secret</li>
              <li>Configure them in the admin settings</li>
            </ol>
            <Button variant="outline" size="sm" asChild>
              <a href="https://console.cloud.google.com" target="_blank" rel="noopener noreferrer">
                <ExternalLink className="h-4 w-4 mr-2" />
                Open Google Cloud Console
              </a>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ApplicationHub;
